import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import type { BadgeTone } from './Badge';

/**
 * Barras de progresso e linhas de metrica (RNF15).
 *
 * `ProgressBar` mostra "enviados de total" com a barra fina do visual premium.
 * `MetricLine` e a linha "18 clicaram no botao": icone, numero em destaque e
 * texto, com fundo tingido pelo tom. E a unidade de leitura dos toques da
 * regua e dos detalhes de campanha; empilhadas, formam a coluna de resultados.
 */

const BAR: Readonly<Record<BadgeTone, string>> = {
  neutral: 'bg-ink-subtle',
  brand: 'bg-brand',
  accent: 'bg-accent',
  info: 'bg-info',
  good: 'bg-status-good',
  warning: 'bg-status-warning',
  critical: 'bg-status-critical',
  violet: 'bg-series-7',
};

export function ProgressBar({
  value,
  max,
  tone = 'brand',
  label,
  className = '',
}: {
  readonly value: number;
  readonly max: number;
  readonly tone?: BadgeTone;
  /** Nome acessivel, por exemplo "Progresso do envio". */
  readonly label: string;
  readonly className?: string;
}): JSX.Element {
  const pct = max <= 0 ? 0 : Math.max(0, Math.min(100, Math.round((value / max) * 100)));
  return (
    <div role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} className={`h-1.5 w-full overflow-hidden rounded-full bg-ink/10 ${className}`}>
      <div className={`h-full rounded-full transition-[width] ${BAR[tone]}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

const LINE: Readonly<Record<BadgeTone, string>> = {
  neutral: 'border-line bg-surface text-ink-muted',
  brand: 'border-brand/30 bg-brand/5 text-brand',
  accent: 'border-accent/30 bg-accent/5 text-accent',
  info: 'border-info/30 bg-info/5 text-info',
  good: 'border-status-good/30 bg-status-good/5 text-status-good',
  warning: 'border-status-warning/40 bg-status-warning/5 text-status-warning',
  critical: 'border-status-critical/30 bg-status-critical/5 text-status-critical',
  violet: 'border-series-7/30 bg-series-7/5 text-series-7',
};

export function MetricLine({
  icon,
  value,
  label,
  detail,
  tone = 'neutral',
  action,
  muted = false,
}: {
  readonly icon: IconName;
  /** Numero ja formatado. */
  readonly value: string;
  readonly label: ReactNode;
  /** Complemento a direita, por exemplo "15,9%". */
  readonly detail?: ReactNode;
  readonly tone?: BadgeTone;
  /** Botao pequeno a direita (lupa, "ver lista"). */
  readonly action?: ReactNode;
  /** Zero ou "ainda sem como medir": some a cor, fica o texto. */
  readonly muted?: boolean;
}): JSX.Element {
  return (
    <div className={`flex items-center gap-3 rounded-lg border px-3 py-2 text-sm ${muted ? LINE.neutral : LINE[tone]}`}>
      <Icon name={icon} size="sm" />
      <p className="min-w-0 flex-1 text-ink">
        <span className={`font-semibold tabular-nums ${muted ? 'text-ink-subtle' : ''}`}>{value}</span> <span className="text-ink-muted">{label}</span>
        {detail === undefined ? null : <span className="text-ink-subtle"> · {detail}</span>}
      </p>
      {action}
    </div>
  );
}

/** Numero pequeno com rotulo em cima: a celula das barras de status (Motor em dia, Ultimo envio...). */
export function StatCell({ label, value, hint, tone = 'neutral' }: { readonly label: string; readonly value: ReactNode; readonly hint?: ReactNode; readonly tone?: BadgeTone }): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 px-4 py-3 first:pl-0 last:pr-0">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">{label}</span>
      <span className={`text-base font-semibold tabular-nums ${tone === 'neutral' ? 'text-ink' : LINE[tone].split(' ').pop() ?? 'text-ink'}`}>{value}</span>
      {hint === undefined ? null : <span className="text-xs text-ink-subtle">{hint}</span>}
    </div>
  );
}
